import { country } from "@features/country/data/services/country.service";
import { user } from "@features/user/data/services/user";
import { getCurrentLocaleCode, localizationEvents } from "@taager/localization";
import { environment } from "environment";
import type mixpanel from "mixpanel-browser";
import packageInfo from "package.json";

export class MixpanelManagerService {
  private mixpanelInstance: typeof mixpanel | null = null;

  private async getInstance(): Promise<typeof mixpanel> {
    if (!this.mixpanelInstance) {
      const module = await import("mixpanel-browser");
      this.mixpanelInstance = module.default;

      this.mixpanelInstance.init(environment.MIXPANEL_PROJECT_TOKEN, {
        api_host: environment.MIXPANEL_PROXY_DOMAIN || undefined,
        // We need to enable it for production only as it consumes a lot of session replays (dev and production shares the same account)
        record_sessions_percent: environment.production ? 1 : 0, // 1%
        record_mask_text_selector: "input[type='password']",
        record_block_selector: "",
      });
    }

    return this.mixpanelInstance;
  }

  public async identify(): Promise<void> {
    const mixpanel = await this.getInstance();
    if (!this.mixpanelCanBeUsedOnUserDevice()) {
      return;
    }

    const merchantId: string = user?.id?.toString();

    mixpanel.identify(merchantId);

    this.setUpUser(merchantId, user.data);
    this.setUpSuperProperties(user);
  }

  private async setUpUser(
    merchantId: string,
    userData: any = {},
  ): Promise<void> {
    const mixpanel = await this.getInstance();

    const userObject = {
      $tager_ID: merchantId,
      $phone: userData?.phoneNum,
      $email: userData?.email,
      $name:
        userData?.fullName || `${userData?.firstName} ${userData?.lastName}`,
      join_date: userData?.createdAt,
    };

    mixpanel.people.set(userObject);
  }

  private async setUpSuperProperties(userData: any = {}): Promise<void> {
    const mixpanel = await this.getInstance();

    const superProperties: any = {
      $email: userData?.email,
      $locale: getCurrentLocaleCode(),
      $version: packageInfo.version,
      $ui_session_key: user.uiSessionKey,
      $selected_country: country.code,
    };
    
    mixpanel.register(superProperties);

    // Watch for locale changes and update the locale in mixpanel
    localizationEvents.onChange("localeCode", () => {
      mixpanel.register({
        $locale: getCurrentLocaleCode(),
      });
    });
  }

  public async track(id: string, action: any = {}): Promise<void> {
    const mixpanel = await this.getInstance();

    if (!this.mixpanelCanBeUsedOnUserDevice()) {
      return;
    }

    // Only users who are logged in should be tracked
    // a Logged in user is either a user who has logged in or signed up (after first phase)
    if (user.isLoggedIn) {
      mixpanel.track(id, action);
    }
  }

  public async reset(): Promise<void> {
    const mixpanel = await this.getInstance();

    if (!this.mixpanelCanBeUsedOnUserDevice()) {
      return;
    }
    mixpanel.reset();
  }

  private async mixpanelCanBeUsedOnUserDevice(): Promise<boolean> {
    let verdict = false;
    try {
      const mixpanel = await this.getInstance();
      mixpanel?.get_distinct_id();
      const hasNotOptedOutTracking = !mixpanel.has_opted_out_tracking();
      verdict = true && hasNotOptedOutTracking;
    } catch {
      // Do nothing
    }

    return verdict;
  }
}

export const mixpanelService = new MixpanelManagerService();
